import type { ElementType, ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/utils/cn";
import { homePlayfair } from "@/components/fonts";

const largeCardTitleStyles = `${homePlayfair.className} mt-0 text-balance text-2xl font-semibold leading-tight tracking-tight lg:text-3xl`;
const mediumCardTitleStyles = `${homePlayfair.className} mt-0 text-balance text-xl font-semibold leading-tight tracking-tight lg:text-2xl`;
const smallCardTitleStyles = `${homePlayfair.className} mt-0 text-balance text-lg font-semibold leading-tight tracking-tight lg:text-xl`;
const compactCardTitleStyles = `${homePlayfair.className} mt-0 text-balance text-base font-semibold leading-snug tracking-tight lg:text-lg`;
const displayCardTitleStyles = `${homePlayfair.className} mt-0 text-balance text-3xl font-semibold leading-none tracking-tight lg:text-4xl`;

const cardTitleToneStyles = {
  default: "text-white",
  subtle: "text-zinc-100",
  muted: "text-zinc-300",
  soft: "text-zinc-400",
} as const;

const cardTitleAlignStyles = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

type CardTitleProps<T extends ElementType = "span"> = {
  title: string;
  titleId?: string | undefined;
  as?: T;
  variant?: "large" | "medium" | "small" | "compact" | "display";
  tone?: "default" | "subtle" | "muted" | "soft";
  align?: "left" | "center" | "right";
  className?: string;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "className" | "children">;

export default function CardTitle<T extends ElementType = "span">({
  title,
  titleId,
  as,
  variant = "large",
  tone = "default",
  align = "left",
  className,
  ...props
}: CardTitleProps<T>) {
  const Component = as || "h3";

  let stylesToUse = largeCardTitleStyles;
  switch (variant) {
    case "large":
      stylesToUse = largeCardTitleStyles;
      break;

    case "medium":
      stylesToUse = mediumCardTitleStyles;
      break;

    case "small":
      stylesToUse = smallCardTitleStyles;
      break;

    case "compact":
      stylesToUse = compactCardTitleStyles;
      break;

    case "display":
      stylesToUse = displayCardTitleStyles;
      break;

    default:
      stylesToUse = largeCardTitleStyles;
  }

  return (
    <Component
      className={cn(
        stylesToUse,
        cardTitleToneStyles[tone],
        cardTitleAlignStyles[align],
        className,
      )}
      id={titleId}
      {...props}
    >
      {title}
    </Component>
  );
}
